import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Logo } from '../ui/Logo';
import { HeaderClickToAction } from '../ui/HeaderClickToAction';
import MobileNavigation from '../ui/MobileNavigation';

interface HeaderProps {
  className?: string;
}

interface NavigationItem {
  id: string;
  label: string;
  href: string;
  color?: string;
}

const NAVIGATION_ITEMS: readonly NavigationItem[] = [
  {
    id: 'mentoring',
    label: 'Mentoring',
    href: '/mentoring',
    color: 'text-secondary-red'
  },
  {
    id: 'newsletter',
    label: 'Newsletter',
    href: '/newsletter',
    color: 'text-secondary-blue'
  },
  {
    id: 'blog',
    label: 'Blog',
    href: '/blog'
  },
  {
    id: 'about',
    label: 'About us',
    href: '/about'
  }
] as const;

export const Header: React.FC<HeaderProps> = ({
  className = ''
}) => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleClickToActionClick = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="relative">
      <header className={`fixed top-0 left-0 right-0 z-50 w-full py-2 sm:py-3 lg:py-4 px-4 sm:px-6 lg:px-8 bg-white dark:bg-neutral-900 shadow ${className}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Left: Logo */}
          <Link to="/" className="flex-shrink-0">
            <Logo showText={false} size="small" className="sm:hidden" />
            <Logo showText={false} size="medium" className="hidden sm:block lg:hidden" />
            <Logo
              isClickable
              firstText="Data"
              secondText="Founder"
              onClick={() => console.log('Logo clicked')}
              className="hidden lg:block"
            />
          </Link>

          {/* Center: Primary Navigation */}
          <nav className="hidden md:flex flex-1 justify-center mx-4 lg:mx-8">
            <ul className="flex items-center space-x-4 lg:space-x-8">
              {NAVIGATION_ITEMS.map(({ id, href, label, color }) => (
                <li key={id}>
                  <Link
                    to={href}
                    className={`text-base sm:text-lg md:text-xl lg:text-xl font-bold ${color || 'text-black dark:text-white'
                      }`}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Right: ClickToAction Buttons - Responsive Design */}
          <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
            <HeaderClickToAction
              variant="mentoring"
              onClick={() => handleClickToActionClick('/mentoring')}
              className="text-sm sm:text-base lg:text-xl px-3 py-1.5 sm:px-4 sm:py-2 lg:px-6 lg:py-3"
            />
            <HeaderClickToAction
              variant="newsletter"
              onClick={() => handleClickToActionClick('/newsletter')}
              className="text-sm sm:text-base lg:text-xl px-3 py-1.5 sm:px-4 sm:py-2 lg:px-6 lg:py-3"
            />
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden ml-4"
            onClick={toggleMobileMenu}
          >
            <span className="sr-only">Toggle menu</span>
            <Menu className="h-6 w-6 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </header>

      {/* Mobile Navigation Dropdown */}
      <MobileNavigation
        isOpen={isMobileMenuOpen}
        onClose={closeMobileMenu}
        navigationItems={NAVIGATION_ITEMS}
      />
    </div>
  );
};

Header.displayName = 'Header';

export type { HeaderProps, NavigationItem };
